import React, { useState, useEffect } from "react";
import "./AssessmentForm.css";
import useAuth from "../../hooks/useAuth";
import { useRef } from 'react';

const AssessmentForm = () => {
  const { authFetch } = useAuth("/login");
  const [activeSection, setActiveSection] = useState(1);

  const radiographyRef = useRef();
  const radiographyNoteRef = useRef();

  const ctRef = useRef();
  const ctNoteRef = useRef();

  const mriRef = useRef();
  const mriNoteRef = useRef();

  const usgRef = useRef();
  const usgNoteRef = useRef();

  const overallNotesRef = useRef();

  // API Data States
  const [modalities, setModalities] = useState([]);
  const [selectedModality, setSelectedModality] = useState(null);
  const [classifications, setClassifications] = useState([]);
  const [submodalities, setSubmodalities] = useState([]);
  const [selectedClassification, setSelectedClassification] = useState(""); // single select
  const [selectedSubs, setSelectedSubs] = useState({}); // Track submodalities
  const [selectAllClassifications, setSelectAllClassifications] = useState(false);
  const [selectedModalities, setSelectedModalities] = useState([]); // Array to track all selected modalities
  const [candidateId, setCandidateId] = useState(null);

  // Section 1 State
  const [formData, setFormData] = useState({
    candidateName: "",
    position: "",
    mobile: "",
    email: "",
    qualification: "",
    medicalRegistration: "",
    experience: "",
    department: "",
    location: "",
    associationType: "",
    timings: ""
  });

  // Section 2 State
  const [personalityData, setPersonalityData] = useState({
    attitude: { rating: "", remarks: "" },
    knowledge: { rating: "", remarks: "" },
    communication: { rating: "", remarks: "" },
    confidence: { rating: "", remarks: "" },
    personality: { rating: "", remarks: "" },
    flexibility: { rating: "", remarks: "" }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRatingChange = (criteria, rating) => {
    setPersonalityData(prev => ({
      ...prev,
      [criteria]: { ...prev[criteria], rating }
    }));
  };

  const handleRemarksChange = (criteria, remarks) => {
    setPersonalityData(prev => ({
      ...prev,
      [criteria]: { ...prev[criteria], remarks }
    }));
  };

  // Fetch APIs on mount
  useEffect(() => {
    // fetch modalities
    fetch("https://internalhrms.vdcapp.in/backend/api/getModalities")
      .then((res) => res.json())
      .then(({ data, status }) => {
        if (status && Array.isArray(data)) setModalities(data);
      })
      .catch(console.error);

    // fetch classifications
    fetch("https://internalhrms.vdcapp.in/backend/api/getSubModClass")
      .then((res) => res.json())
      .then(({ data, status }) => {
        if (status && Array.isArray(data)) {
          setClassifications(data);
        }
      })
      .catch(console.error);

    // fetch all submodalities
    fetch("https://internalhrms.vdcapp.in/backend/api/getSubModalities")
      .then((res) => res.json())
      .then(({ data, status }) => {
        if (status && Array.isArray(data)) setSubmodalities(data);
      })
      .catch(console.error);
  }, []);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const handleSave = (section) => {
    if (section === 3) {
      // Prepare data for saving - include both current selection and all saved modalities
      const saveData = {
        currentSelection: {
          modalityId: selectedModality,
          classificationId: selectedClassification,
          selectAll: selectAllClassifications,
          subSelections: selectedSubs
        },
        allSelectedModalities: selectedModalities
      };

      console.log("Saving Section 3 data:", saveData);
      // Here you would send saveData to your API instead of console.log
    }

    /*alert(`Section ${section} data saved!`);*/
  };

  // Helpers
  const getUniqueKey = (modalityId, classification) =>
    `${modalityId}-${classification}`;

  const toggleSub = (modalityId, cls, sub) => {
    const uniqueKey = getUniqueKey(modalityId, cls);
    setSelectedSubs((prev) => {
      const clsSubs = prev[uniqueKey] || [];
      if (clsSubs.includes(sub)) {
        return { ...prev, [uniqueKey]: clsSubs.filter((s) => s !== sub) };
      } else {
        return { ...prev, [uniqueKey]: [...clsSubs, sub] };
      }
    });
  };

  const toggleSelectAll = (modalityId, cls, subs) => {
    const uniqueKey = getUniqueKey(modalityId, cls);
    setSelectedSubs((prev) => {
      const clsSubs = prev[uniqueKey] || [];
      if (clsSubs.length === subs.length) {
        return { ...prev, [uniqueKey]: [] }; // uncheck all
      } else {
        return { ...prev, [uniqueKey]: subs }; // check all
      }
    });
  };

  const isAllSelected = (modalityId, cls, subs) => {
    const uniqueKey = getUniqueKey(modalityId, cls);
    return (selectedSubs[uniqueKey] || []).length === subs.length;
  };

  // Handle select all classifications
  const handleSelectAllClassifications = () => {
    if (selectAllClassifications) {
      // Clear all selected classifications
      setSelectedClassification("");
      setSelectedSubs({});
    } else {
      // Select all classifications
      if (selectedModality) {
        // For each classification, select all its submodalities
        const newSelectedSubs = {};
        classifications.forEach(cls => {
          const filteredSubs = submodalities.filter(
            sub => String(sub.mod_id) === String(selectedModality) &&
              String(sub.sub_mod_class) === String(cls.id)
          );
          if (filteredSubs.length > 0) {
            const uniqueKey = getUniqueKey(selectedModality, cls.id);
            newSelectedSubs[uniqueKey] = filteredSubs.map(s => s.sub_mod_name);
          }
        });
        setSelectedSubs(newSelectedSubs);
      }
    }
    setSelectAllClassifications(!selectAllClassifications);
  };

  // Add a new modality selection
  const addNewModality = () => {
    if (selectedModality) {
      setSelectedModalities(prev => [...prev, {
        id: Date.now(), // unique ID for this modality selection
        modalityId: selectedModality,
        classificationId: selectedClassification,
        selectAll: selectAllClassifications,
        subSelections: { ...selectedSubs } // copy current sub selections
      }]);

      // Reset current selection
      setSelectedModality("");
      setSelectedClassification("");
      setSelectAllClassifications(false);
      setSelectedSubs({});
    }
  };

  // Remove a modality selection
  const removeModality = (id) => {
    setSelectedModalities(prev => prev.filter(item => item.id !== id));
  };

  const handleSaveSection1 = async () => {
    try {
      const section1Data = {
        candidate_name: formData.candidateName,
        position_applied: formData.position,
        mobile: formData.mobile,
        email: formData.email,
        education_qualification: formData.qualification,
        medical_registration: formData.medicalRegistration,
        total_experience: formData.experience,
        department: formData.department,
        vdc_location: formData.location,
        association_type: formData.associationType,
        timing: formData.timings
      };

      console.log('Sending payload:', section1Data);

      const response = await authFetch('https://internalhrms.vdcapp.in/backend/api/createRadiologyCandidate', {
        method: 'POST',
        body: JSON.stringify(section1Data)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries([...response.headers]));

      const responseText = await response.text(); // Get raw text first
      console.log('Raw response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText); // Try to parse as JSON
        console.log('Parsed JSON response:', result);
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        console.log('Response is not JSON, raw text:', responseText);
        throw new Error('Server returned non-JSON response');
      }

      if (result.status) {
        console.log('✅ SUCCESS - Full response data:', result);

        // Check different possible locations for candidate_id
        const candidateId =
          result.data?.candidate_id ||
          result.data?.id ||
          result.candidate_id ||
          result.id;

        console.log('Candidate ID found:', candidateId);

        if (candidateId) {
          // Store it for Section 2
          setCandidateId(candidateId);
          alert('Section 1 data saved successfully! Candidate ID: ' + candidateId);
        } else {
          alert('Section 1 saved but no candidate ID returned. Check console for details.');
        }
      } else {
        console.log('❌ API returned status false:', result);
        alert('Error saving data: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('API Error details:', error);
      console.error('Error stack:', error.stack);
      alert('Failed to save data. Please check console for details.');
    }
  };

  const handleSaveSection2 = async () => {
    try {
      if (!candidateId) {
        alert('Please save Section 1 first to get candidate ID');
        return;
      }

      const section2Data = {
        candidate_id: candidateId.toString(), // String conversion
        assessment_date: new Date().toISOString().split('T')[0], // ✅ "2025-09-11" format
        criteria: [
          "Attitude",
          "Knowledge",
          "Communication",
          "Confidence",
          "Personality",
          "Flexibility"
        ],
        rating: [
          parseInt(personalityData.attitude.rating) || 0,
          parseInt(personalityData.knowledge.rating) || 0,
          parseInt(personalityData.communication.rating) || 0,
          parseInt(personalityData.confidence.rating) || 0,
          parseInt(personalityData.personality.rating) || 0,
          parseInt(personalityData.flexibility.rating) || 0
        ],
        remarks: [
          personalityData.attitude.remarks,
          personalityData.knowledge.remarks,
          personalityData.communication.remarks,
          personalityData.confidence.remarks,
          personalityData.personality.remarks,
          personalityData.flexibility.remarks
        ]
      };

      console.log('Sending Section 2 payload:', section2Data); // For debugging

      const response = await authFetch('https://internalhrms.vdcapp.in/backend/api/createRadiologyPersonalityAssessment', {
        method: 'POST',
        body: JSON.stringify(section2Data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        alert('Section 2 data saved successfully!');
      } else {
        alert('Error saving data: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('API Error:', error);
      alert('Failed to save data. Please try again.');
    }
  };


  const handleSaveSection3 = async () => {
    if (!selectedModality || Object.keys(selectedSubs).length === 0) {
      console.warn("No submodalities selected.");
      return;
    }

    console.log("selectedModality", selectedModality);
    console.log("selectedClassification", selectedClassification);
    console.log("selectedSubs", selectedSubs);

    const payload = [];

    Object.entries(selectedSubs).forEach(([key, subs]) => {
      const [, classificationId] = key.split("-");
      const classification = classifications.find(
        (c) => String(c.id) === classificationId
      );

      const matchedSubmodalities = submodalities.filter(
        (sub) =>
          String(sub.mod_id) === String(selectedModality) &&
          String(sub.sub_mod_class) === String(classificationId) &&
          subs.includes(sub.sub_mod_name)
      );

      matchedSubmodalities.forEach((submod) => {
        payload.push({
          candidate_id: candidateId.toString(),
          mod_id: selectedModality,
          sub_mod_id: submod.id,
          sub_mod_class: classification?.id || null,
        });
      });
    });

    console.log("🔼 Payload to Submit:", payload);
    const token = sessionStorage.getItem('token') || localStorage.getItem('authToken');
    // ✅ Optional: Send to API
    try {
      const response = await fetch("https://internalhrms.vdcapp.in/backend/api/createRadiologyModalities", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Uncomment if required
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("✅ API Response:", result);

      if (result.status) {
        alert("Radiology modalities saved successfully!");
      } else {
        alert("Failed to save: " + result.message);
      }
    } catch (error) {
      console.error("❌ Error submitting data:", error);
      alert("Something went wrong while submitting.");
    }
  };


  const handleSaveSection4 = async () => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('authToken');

    const payload = {
      candidate_id: candidateId.toString(),
      evaluation: [
        {
          particular: "Radiography Reporting",
          assessment: radiographyRef.current.value,
          notes: radiographyNoteRef.current.value,
        },
        {
          particular: "CT Scans Reporting",
          assessment: ctRef.current.value,
          notes: ctNoteRef.current.value,
        },
        {
          particular: "MRI Scans Reporting",
          assessment: mriRef.current.value,
          notes: mriNoteRef.current.value,
        },
        {
          particular: "USG Scanning",
          assessment: usgRef.current.value,
          notes: usgNoteRef.current.value,
        },
      ],
      overall_notes: overallNotesRef.current.value,
    };

    console.log("🔼 Technical Evaluation Payload:", payload);

    try {
      const response = await fetch("https://internalhrms.vdcapp.in/backend/api/createRadiologyTechnicalEvaluation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("✅ API Response:", result);

      if (result.status) {
        alert("Technical evaluation saved successfully!");
      } else {
        alert("Failed to save: " + result.message);
      }
    } catch (error) {
      console.error("❌ Error submitting data:", error);
      alert("Something went wrong while submitting.");
    }
  };


 const handleSaveSection5 = async () => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('authToken');

  // Only allow these keys
  const regionMap = {
    region1: "AP & Telangana",
    region2: "Kolkata",
    region3: "Pune",
  };

  // ✅ Prepare the payload: only known region keys from regionMap
  const payload = {
    candidate_id: candidateId,
    regions: Object.entries(selectedSubs)
      .filter(([key, branches]) => regionMap[key] && branches.length > 0) // ✅ valid & not empty
      .map(([key, branches]) => ({
        region: regionMap[key], // ✅ use mapped name only
        branches,
      })),
  };

  // ✅ Check if anything is selected
  if (payload.regions.length === 0) {
    alert("Please select at least one branch.");
    return;
  }

  try {
    const res = await authFetch(
      `${process.env.REACT_APP_BASE_URL}/createRadiologyRegion`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    const result = await res.json();

    if (result.status) {
      alert("Branch access saved successfully!");
    } else {
      alert(result.message || "Failed to save branch access.");
    }
  } catch (err) {
    console.error("Error saving branch access:", err);
    alert("Something went wrong while saving.");
  }
};




  const handleSaveSection3_old = async () => {
    try {
      if (!candidateId) {
        alert('Please save Section 1 & 2 first to get candidate ID');
        return;
      }

      const section3Data = {
        candidate_id: candidateId.toString(), // String conversion
        assessment_date: new Date().toISOString().split('T')[0], // ✅ "2025-09-11" format
        criteria: [
          "Attitude",
          "Knowledge",
          "Communication",
          "Confidence",
          "Personality",
          "Flexibility"
        ],
        rating: [
          parseInt(personalityData.attitude.rating) || 0,
          parseInt(personalityData.knowledge.rating) || 0,
          parseInt(personalityData.communication.rating) || 0,
          parseInt(personalityData.confidence.rating) || 0,
          parseInt(personalityData.personality.rating) || 0,
          parseInt(personalityData.flexibility.rating) || 0
        ],
        remarks: [
          personalityData.attitude.remarks,
          personalityData.knowledge.remarks,
          personalityData.communication.remarks,
          personalityData.confidence.remarks,
          personalityData.personality.remarks,
          personalityData.flexibility.remarks
        ]
      };

      console.log('Sending Section 2 payload:', section3Data); // For debugging

      const response = await authFetch('https://internalhrms.vdcapp.in/backend/api/createRadiologyPersonalityAssessment', {
        method: 'POST',
        body: JSON.stringify(section3Data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        alert('Section 2 data saved successfully!');
      } else {
        alert('Error saving data: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('API Error:', error);
      alert('Failed to save data. Please try again.');
    }
  };

  return (
    <div className="assessment-form">
      {/* <h2 className="form-title">Radiology Assessment Form</h2> */}

      {/* Section 1 - Candidate Information */}
      <div className="accordion-section">
        <button className="accordion-header" onClick={() => toggleSection(1)}>
          Candidate Information
        </button>
        {activeSection === 1 && (
          <div className="accordion-content">
            <div className="form-grid">
              <div className="form-group">
                <label>Candidate Name</label>
                <input type="text" placeholder="Enter candidate name" className="form-control"
                  value={formData.candidateName}
                  onChange={(e) => handleInputChange('candidateName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Position Applied</label>
                <input type="text" placeholder="Enter position" className="form-control"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="tel"
                  placeholder="Enter mobile number" className="form-control"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength="10"
                  value={formData.mobile}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('mobile', e.target.value);
                  }} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="Enter email" className="form-control"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Educational Qualification</label>
                <input type="text" placeholder="Enter qualification" className="form-control"
                  value={formData.qualification}
                  onChange={(e) => handleInputChange('qualification', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Relevant State Medical Registration</label>
                <select className="form-control"
                  value={formData.medicalRegistration}
                  onChange={(e) => handleInputChange('medicalRegistration', e.target.value)}>
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="form-group">
                <label>Total Experience</label>
                <input type="number" placeholder="Enter years" className="form-control"
                  value={formData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  min="0" max="50" />
              </div>
              <div className="form-group">
                <label>Department</label>
                <input type="text" placeholder="Enter department" className="form-control"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)} />
              </div>
              <div className="form-group">
                <label>VDC Location</label>
                <input type="text" placeholder="Enter location" className="form-control"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Type of Association</label>
                <select className="form-control"
                  value={formData.associationType}
                  onChange={(e) => handleInputChange('associationType', e.target.value)}>
                  <option value="">Select</option>
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                  <option value="Target Model">Target Model</option>
                  <option value="Base">Base</option>
                </select>
              </div>
              <div className="form-group">
                <label>Timings</label>
                <input type="text" placeholder="Enter timings" className="form-control"
                  value={formData.timings}
                  onChange={(e) => handleInputChange('timings', e.target.value)} />
              </div>
            </div>
            <div className="section-actions">
              <button className="save-btn" onClick={handleSaveSection1}>
                Save Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section 2 - Personality Assessment */}
      <div className="accordion-section">
        <button className="accordion-header" onClick={() => toggleSection(2)}>
          Personality Assessment
        </button>
        {activeSection === 2 && (
          <div className="accordion-content">
            <p className="rating-reference">
              Ratings Reference: Outstanding(4) | Exceeds Expectation(3) | Meets
              Expectation(2) | Does not meet Expectation(1)
            </p>
            <div className="table-container">
              <table className="assessment-table">
                <thead>
                  <tr>
                    <th>Criteria</th>
                    <th>Rating (1–4)</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'attitude', label: 'Attitude | Commitment to offered role' },
                    { key: 'knowledge', label: 'Relevant Job Knowledge' },
                    { key: 'communication', label: 'Communication Skills' },
                    { key: 'confidence', label: 'Confidence' },
                    { key: 'personality', label: 'Personality' },
                    { key: 'flexibility', label: 'Flexibility to work at different VDC Centres' },
                  ].map(({ key, label }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>
                        <select
                          className="form-control"
                          value={personalityData[key].rating}
                          onChange={(e) => handleRatingChange(key, e.target.value)}>
                          <option value="">Select Rating</option>
                          {[1, 2, 3, 4].map((num) => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          placeholder="Enter remarks"
                          className="form-control"
                          value={personalityData[key].remarks}
                          onChange={(e) => handleRemarksChange(key, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="section-actions">
              <button className="save-btn" onClick={handleSaveSection2}>
                Save Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section 3 - Dynamic Relevant Modalities */}
      <div className="accordion-section">
        <button className="accordion-header" onClick={() => toggleSection(3)}>
          Assessment of Relevant Modalities
        </button>
        {activeSection === 3 && (
          <div className="accordion-content">
            <div className="modality-row">
              {/* Dropdown 1 - Modality */}
              <div className="form-group">
                <label>Select Modality</label>
                <select
                  className="form-control"
                  value={selectedModality || ""}
                  onChange={(e) => {
                    setSelectedModality(e.target.value);
                    setSelectedClassification(""); // reset classification
                    setSelectAllClassifications(false); // reset select all
                  }}
                >
                  <option value="">-- Select Modality --</option>
                  {modalities.map((mod) => (
                    <option key={String(mod.id)} value={String(mod.id)}>
                      {mod.mod_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dropdown 2 - Classification */}
              <div className="form-group">
                <label>Select Classification</label>
                <div className="classification-dropdown">
                  <div className="select-all-classifications">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectAllClassifications}
                        onChange={handleSelectAllClassifications}
                        disabled={!selectedModality}
                      />
                      Select All Classifications
                    </label>
                  </div>
                  <select
                    className="form-control"
                    value={selectedClassification || ""}
                    onChange={(e) => {
                      setSelectedClassification(e.target.value);
                      setSelectAllClassifications(false);
                    }}
                    disabled={!selectedModality || selectAllClassifications}
                  >
                    <option value="">
                      {selectedModality
                        ? "-- Select Classification --"
                        : "Select modality first"}
                    </option>
                    {selectedModality &&
                      classifications.map((cls) => (
                        <option key={String(cls.id)} value={String(cls.id)}>
                          {cls.sub_mod_class}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Submodalities Block */}
              <div className="submodalities-block">
                <h4>Submodalities</h4>
                {selectedModality && (selectedClassification || selectAllClassifications) ? (
                  selectAllClassifications ? (
                    // Show all submodalities for all classifications when "Select All" is checked
                    (() => {
                      const allFilteredSubs = classifications.map(cls => {
                        const filteredSubs = submodalities.filter(
                          sub => String(sub.mod_id) === String(selectedModality) &&
                            String(sub.sub_mod_class) === String(cls.id)
                        );
                        return { cls, subs: filteredSubs };
                      }).filter(item => item.subs.length > 0);

                      return (
                        <>
                          {allFilteredSubs.map(({ cls, subs }) => {
                            const uniqueKey = getUniqueKey(selectedModality, cls.id);
                            const allSelected = isAllSelected(selectedModality, cls.id, subs.map(s => s.sub_mod_name));

                            return (
                              <div key={cls.id} className="classification-group">
                                <div className="classification-header">
                                  <h5>{cls.sub_mod_class}</h5>
                                  <div className="checkbox-item">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={() =>
                                          toggleSelectAll(
                                            selectedModality,
                                            cls.id,
                                            subs.map(s => s.sub_mod_name)
                                          )
                                        }
                                      />
                                      <strong>Select All</strong>
                                    </label>
                                  </div>
                                </div>
                                <div className="submodalities-list">
                                  {subs.map((sub) => (
                                    <div key={sub.id} className="checkbox-item">
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={(selectedSubs[uniqueKey] || []).includes(
                                            sub.sub_mod_name
                                          )}
                                          onChange={() =>
                                            toggleSub(
                                              selectedModality,
                                              cls.id,
                                              sub.sub_mod_name
                                            )
                                          }
                                        />
                                        {sub.sub_mod_name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {allFilteredSubs.length === 0 && (
                            <p>No submodalities available for this modality.</p>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    // Show submodalities for a single classification
                    (() => {
                      const filteredSubs = submodalities.filter(
                        (sub) =>
                          String(sub.mod_id) === String(selectedModality) &&
                          String(sub.sub_mod_class) === String(selectedClassification)
                      );
                      const uniqueKey = getUniqueKey(
                        selectedModality,
                        selectedClassification
                      );
                      const allSelected = isAllSelected(
                        selectedModality,
                        selectedClassification,
                        filteredSubs.map(s => s.sub_mod_name)
                      );

                      return (
                        <>
                          {filteredSubs.length > 0 && (
                            <div className="checkbox-item">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  onChange={() =>
                                    toggleSelectAll(
                                      selectedModality,
                                      selectedClassification,
                                      filteredSubs.map((s) => s.sub_mod_name)
                                    )
                                  }
                                />
                                <strong>Select All</strong>
                              </label>
                            </div>
                          )}
                          <div className="submodalities-list">
                            {filteredSubs.map((sub) => (
                              <div key={sub.id} className="checkbox-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={(selectedSubs[uniqueKey] || []).includes(
                                      sub.sub_mod_name
                                    )}
                                    onChange={() =>
                                      toggleSub(
                                        selectedModality,
                                        selectedClassification,
                                        sub.sub_mod_name
                                      )
                                    }
                                  />
                                  {sub.sub_mod_name}
                                </label>
                              </div>
                            ))}
                          </div>
                          {filteredSubs.length === 0 && (
                            <p>No submodalities available for this selection.</p>
                          )}
                        </>
                      );
                    })()
                  )
                ) : (
                  <p>Please select modality and classification.</p>
                )}
              </div>
            </div>

            {selectedModality && (selectedClassification || selectAllClassifications) && (
              <div className="add-modality-btn-container">
                <button
                  className="add-modality-btn"
                  onClick={addNewModality}
                >
                  + Add Another Modality
                </button>
              </div>
            )}

            {selectedModalities.length > 0 && (
              <div className="selected-modalities">
                <h4>Selected Modalities:</h4>
                {selectedModalities.map((modalityItem) => {
                  const modality = modalities.find(m => String(m.id) === String(modalityItem.modalityId));
                  const classificationName = modalityItem.selectAll
                    ? "Classifications"
                    : classifications.find(c => String(c.id) === String(modalityItem.classificationId))?.sub_mod_class;

                  return (
                    <div key={modalityItem.id} className="selected-modality-item">
                      <div className="modality-info">
                        <strong>{modality?.mod_name}</strong> - <strong>{"Classifications"}</strong>
                        <button
                          className="remove-btn"
                          onClick={() => removeModality(modalityItem.id)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="selected-subs">
                        {Object.entries(modalityItem.subSelections).map(([key, subs]) => {
                          // Extract classification ID from the key
                          const [, classificationId] = key.split('-');
                          const classification = classifications.find(c => String(c.id) === classificationId);

                          return (
                            <div key={key} className="sub-list">
                              <span className="sub-classification">{classification?.sub_mod_class || 'Unknown'}: </span>
                              {subs.join(", ")}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="section-actions">
              <button className="save-btn" onClick={handleSaveSection3}>
                Save Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section 4 - Technical Evaluation */}
      <div className="accordion-section">
        <button className="accordion-header" onClick={() => toggleSection(4)}>
          Technical Evaluation
        </button>
        {activeSection === 4 && (
          <div className="accordion-content">
            <div className="table-container">
              <table className="assessment-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Assessment</th>
                    <th>Notes (if any)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Radiography Reporting</td>
                    <td>
                      <select className="form-control" ref={radiographyRef}>
                        <option value="">Select</option>
                        <option value="Good">Good</option>
                        <option value="Satisfactory">Satisfactory</option>
                        <option value="Dissatisfactory">Dissatisfactory</option>
                      </select>
                    </td>
                    <td>
                      <textarea
                        rows="4"
                        placeholder="Enter notes"
                        className="form-control"
                        defaultValue="For Radiography Chest X-Ray & Other X-ray images shall be shared and the report shall be obtained from the Doctor"
                        ref={radiographyNoteRef}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>CT Scans Reporting</td>
                    <td>
                      <select className="form-control" ref={ctRef}>
                        <option value="">Select</option>
                        <option value="Good">Good</option>
                        <option value="Satisfactory">Satisfactory</option>
                        <option value="Dissatisfactory">Dissatisfactory</option>
                      </select>
                    </td>
                    <td>
                      <textarea
                        rows="4"
                        placeholder="Enter notes"
                        className="form-control"
                        defaultValue="For CT Brain & Other CT images shall be shared and the report shall be obtained from the Doctor"
                        ref={ctNoteRef}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>MRI Scans Reporting</td>
                    <td>
                      <select className="form-control" ref={mriRef}>
                        <option value="">Select</option>
                        <option value="Good">Good</option>
                        <option value="Satisfactory">Satisfactory</option>
                        <option value="Dissatisfactory">Dissatisfactory</option>
                      </select>
                    </td>
                    <td>
                      <textarea
                        rows="4"
                        placeholder="Enter notes"
                        className="form-control"
                        defaultValue="For MRI Brain & MRI Knee images shall be shared and the report shall be obtained from the Doctor"
                        ref={mriNoteRef}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>USG Scanning</td>
                    <td>
                      <select className="form-control" ref={usgRef}>
                        <option value="">Select</option>
                        <option value="Good">Good</option>
                        <option value="Satisfactory">Satisfactory</option>
                        <option value="Dissatisfactory">Dissatisfactory</option>
                      </select>
                    </td>
                    <td>
                      <textarea
                        rows="2"
                        placeholder="Enter notes"
                        className="form-control"
                        ref={usgNoteRef}
                      />
                    </td>
                  </tr>
                </tbody>

              </table>
            </div>

            <div className="accordion-content">
              <div className="form-group full-width">
                <label><strong>Overall Evaluation Notes</strong></label>
                <textarea
                  rows="6"
                  placeholder="Enter overall evaluation notes here..."
                  className="form-control"
                  ref={overallNotesRef}
                />
              </div>
            </div>

            <div className="section-actions">
              <button className="save-btn" onClick={handleSaveSection4}>
                Save Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section 5 - Overall Evaluation Notes */}
      {/*<div className="accordion-section">
  <button className="accordion-header" onClick={() => toggleSection(5)}>
    Overall Evaluation Notes
  </button>
  {activeSection === 5 && (
    <div className="accordion-content">
      <div className="form-group full-width">
        <label>Overall Evaluation Notes</label>
        <textarea
          rows="6"
          placeholder="Enter overall evaluation notes here..."
          className="form-control"
        />
      </div>
      <div className="section-actions">
        <button className="save-btn" onClick={() => handleSave(5)}>
          Save Section
        </button>
      </div>
    </div>
  )}
		  </div>*/}

      {/* Section 6 - Relevant Branch Access */}
      <div className="accordion-section">
        <button className="accordion-header" onClick={() => toggleSection(6)}>
          Relevant Branch Access
        </button>
        {activeSection === 6 && (
          <div className="accordion-content">
            <div className="branch-access-container">
              <p className="branch-access-description">
                Select the regions and branches where access should be granted:
              </p>

              <div className="branch-access-grid">
                {[
                  { id: "region1", name: "AP & Telangana", branches: ["Himayathnagar", "Panjagutta", "Vizag", "Tirupathi", "Kurnool", "Motinagar",] },
                  { id: "region2", name: "Kolkata", branches: ["VIP Road", "Barasat", "Kasba", "Krishna Nagar"] },
                  { id: "region3", name: "Pune", branches: ["Satara", "Hadapsar", "Mayur Colony", "Kalyani Nagar"] },
                ].map((region) => {
                  const allSelected = region.branches.every((b) =>
                    (selectedSubs[region.id] || []).includes(b)
                  );

                  return (
                    <div key={region.id} className="region-card">
                      <div className="region-card-header">
                        <label className="region-select-all">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => {
                              if (allSelected) {
                                setSelectedSubs((prev) => ({
                                  ...prev,
                                  [region.id]: [],
                                }));
                              } else {
                                setSelectedSubs((prev) => ({
                                  ...prev,
                                  [region.id]: [...region.branches],
                                }));
                              }
                            }}
                          />
                          {region.name}
                          <span className="region-badge">
                            {(selectedSubs[region.id] || []).length}/{region.branches.length}
                          </span>
                        </label>
                      </div>

                      <div className="branch-list">
                        {region.branches.map((branch, idx) => (
                          <label key={idx} className="branch-checkbox">
                            <input
                              type="checkbox"
                              checked={(selectedSubs[region.id] || []).includes(branch)}
                              onChange={() => {
                                setSelectedSubs((prev) => {
                                  const current = prev[region.id] || [];
                                  if (current.includes(branch)) {
                                    return {
                                      ...prev,
                                      [region.id]: current.filter((b) => b !== branch),
                                    };
                                  } else {
                                    return {
                                      ...prev,
                                      [region.id]: [...current, branch],
                                    };
                                  }
                                });
                              }}
                            />
                            {branch}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="section-actions">
                <button className="save-btn" onClick={handleSaveSection5}>
                  Save Section
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentForm;